<template>
    <div class="flex flex-col gap-2">
        <div v-if="canManageProperty" class="flex gap-10 rounded-xl justify-center bg-[var(--c-primary)] p-4">
            <ButtonWhite @click="openDeleteConfirmModal">{{ $t('property.detail.buttons.delete') }}</ButtonWhite>
            <ButtonWhite @click="showChangeAccountModal">{{ $t('property.detail.buttons.changeAccount') }}</ButtonWhite>
            <RouterLink :to="{ path: `/Property-new/${id}`, query: { type: fincaData?.type === 'inmueble' ? 'inmueble' : 'comunidad' } }">
                <ButtonWhite>{{ $t('property.detail.buttons.modify') }}</ButtonWhite>
            </RouterLink>
        </div>

        <div class="flex flex-col gap-8 pb-50">
            <titlePrimary :title="fincaData?.type === 'inmueble' ? $t('property.detail.estateTitle') : $t('property.detail.title')" class="w-full" />
            <div v-if="loading" class="text-center p-4">{{ $t('property.detail.loading') }}</div>
            <div v-else-if="error" class="text-red-500 text-center p-4">{{ error }}</div>

            <div v-else-if="fincaData" class="contents">
                <!-- Datos del titular, iguales en la comunidad y en el inmueble (ADS-0170):
                     nombre, documento, email y teléfono, como en el alta. La ubicación y
                     los CUPS van en las tablas de suministros. "Editar" abre el formulario
                     de la finca; la comunidad solo la edita ADS. -->
                <div class="relative p-8 rounded-xl border shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]" :data-testid="isEstate ? 'estate-data' : 'community-data'">
                    <RouterLink v-if="canEditProperty" :to="{ path: `/Property-new/${id}`, query: { type: propertyType } }" data-testid="edit-estate"
                        class="absolute top-3 right-6 text-xs text-[#2AB4D9] underline hover:text-[#25B0D7]">
                        {{ $t('property.estateSupplies.edit') }}
                    </RouterLink>
                    <div class="grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-6 pt-2">
                        <inputPrimary :modelValue="estateHolderName(fincaData, propertyType)" :label="$t('property.estateForm.fields.ownerName')" class="p-4" readonly />
                        <inputPrimary :modelValue="fincaData.cif ?? '-'" :label="$t('property.estateForm.fields.ownerDocument')" class="p-4" readonly />
                        <inputPrimary :modelValue="fincaData.owner_email ?? fincaData.propietario?.email ?? '-'" :label="$t('property.estateForm.fields.ownerEmail')" class="p-4" readonly />
                        <inputPrimary :modelValue="fincaData.telephone ?? '-'" :label="$t('property.estateForm.fields.ownerPhone')" class="p-4" readonly />
                    </div>
                </div>

                <titlePrimary :title="$t('property.communityForm.sections.additional')" class="w-full" />
                <div class="grid md:grid-cols-2 gap-4 p-4 rounded-xl border shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
                    <textareaPrimary :modelValue="fincaData.comments ?? '-'" :label="$t('property.detail.labels.comments')"
                        class="p-4" :rows="4" readonly />
                    <textareaPrimary :modelValue="fincaData.other ?? '-'" :label="$t('property.detail.labels.other')"
                        class="p-4" :rows="4" readonly />
                </div>

                <titlePrimary :title="$t('property.detail.sections.files')" class="w-full" />
                <div v-if="fincaData.files && fincaData.files.length > 0">
                    <TableComponent :fields="files" :items="fincaData.files" :fileDownload="true" :showOptions="true" />
                </div>
                <div v-else class="text-center p-4 text-gray-500">
                    {{ $t('property.detail.labels.noFiles') }}
                </div>

                <!-- Suministros (ADS-0124, ADS-0170): CUPS, dirección, CP, municipio, provincia, tarifa y
                     potencias (solo luz), con alta desde la propia ficha. La edición del CUPS de una
                     comunidad queda para ADS: para el cliente la API la rechaza. -->
                <template v-for="energy in ['light', 'gas']" :key="energy">
                    <div class="flex flex-col md:flex-row md:items-center gap-4">
                        <titlePrimary :title="$t(`property.detail.sections.${energy}`)" class="w-full" />
                        <ButtonWhite class="whitespace-nowrap" :data-testid="`add-${energy}`" @click="openSupplyModal(energy)">
                            {{ $t(`property.estateSupplies.add.${energy}`) }}
                        </ButtonWhite>
                    </div>
                    <div v-if="supplyRows[energy].length > 0">
                        <TableComponent :fields="supplyColumns[energy]" :items="supplyRows[energy]" :showOptions="true"
                            :listOptions="optionsList">
                            <template #cell-cups="{ item }">
                                <span class="flex items-center gap-2">
                                    <RouterLink :to="`/${energy}-detail/${item.id}`"
                                        class="text-blue-600 underline hover:text-blue-800">
                                        {{ item.cups }}
                                    </RouterLink>
                                    <button v-if="canEditSupply" type="button" class="text-xs text-[#2AB4D9] underline hover:text-[#25B0D7]"
                                        :title="$t('property.estateSupplies.edit')" @click.stop="openSupplyModal(energy, item)">
                                        {{ $t('property.estateSupplies.edit') }}
                                    </button>
                                </span>
                            </template>
                        </TableComponent>
                    </div>
                    <div v-else class="text-center p-2 text-gray-500">
                        {{ $t(energy === 'light' ? 'property.detail.labels.noLight' : 'property.detail.labels.noGas') }}
                    </div>
                </template>
            </div>
            <div v-else class="text-center p-4">{{ $t('property.detail.labels.noData') }}</div>
        </div>

        <ChangeAccountModal :isOpen="showModal" :property-id="id" @close="handleCloseModal"
            @account-changed="fetchFincaDetail" />
        <EstateSupplyModal :isOpen="supplyModal.open" :property="fincaData" :type="supplyModal.type"
            :supply="supplyModal.supply" @close="supplyModal.open = false" @saved="fetchFincaDetail" />

        <div v-if="showDeleteConfirmModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div class="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-lg">
                <h3 class="text-lg font-semibold text-[#385660] mb-2">Confirmar eliminación</h3>
                <p class="text-gray-600 mb-6">¿Seguro que quieres eliminar esta comunidad? Esta acción no se puede deshacer.</p>
                <div class="flex justify-end gap-3">
                    <button
                        @click="closeDeleteConfirmModal"
                        :disabled="isDeleting"
                        class="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    >
                        Cancelar
                    </button>
                    <button
                        @click="deleteFinca"
                        :disabled="isDeleting"
                        class="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                    >
                        {{ isDeleting ? 'Eliminando...' : 'Eliminar' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import titlePrimary from '@/components/title/title-primary.vue';

import inputPrimary from '@/components/inputs/input-primary.vue';
import textareaPrimary from '@/components/inputs/textarea-primary.vue';
import TableComponent from '@/components/table/TableComponent.vue';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { supplyRequestOptions } from '@/services/supplyRequestOptions';
import axios from 'axios';
import { useRoute } from 'vue-router';
import ButtonWhite from '@/components/button/ButtonWhite.vue';
import ChangeAccountModal from '@/components/modal/ChangeAccountModal.vue';
import EstateSupplyModal from '@/components/supply/EstateSupplyModal.vue';
import { estateSupplyColumns, estateSupplyRows } from '@/services/estateSupplies';
import { estateHolderName } from '@/services/estateHolder';
import { getUserRoles, isAdsUser } from '@/services/userRoles';
import { useRouter } from 'vue-router';


const route = useRoute();
const id = route.params.id;
const { t } = useI18n();

const files = [
    { key: "date", label: "Fecha" },
    { key: "type", label: "Tipo" },
    { key: "name", label: "Nombre" },
    { key: "user", label: "Usuario" },
    { key: "opciones", label: "Opciones" },
];

// Comunidad e inmueble muestran el mismo resumen del suministro (ADS-0124, ADS-0170).
// Tarifa y compañía del contrato siguen en el detalle de cada CUPS.
const isEstate = computed(() => fincaData.value?.type === 'inmueble');
const propertyType = computed(() => (isEstate.value ? 'inmueble' : 'comunidad'));
const internalUser = isAdsUser();
const canManageProperty = computed(() => getUserRoles().some((role) => ['superadmin', 'admin'].includes(role)));
// La comunidad y sus CUPS son de solo consulta para el cliente (API 403); el inmueble lo edita su titular.
const canEditProperty = computed(() => isEstate.value || internalUser);
const canEditSupply = computed(() => isEstate.value || internalUser);
const supplyColumns = computed(() => ({
    light: estateSupplyColumns('light', t),
    gas: estateSupplyColumns('gas', t),
}));
const supplyRows = computed(() => ({
    light: estateSupplyRows(fincaData.value?.lights, fincaData.value),
    gas: estateSupplyRows(fincaData.value?.gas, fincaData.value),
}));

// Añadir o editar un suministro del inmueble sin salir de la ficha.
const supplyModal = ref({ open: false, type: 'light', supply: null });
const openSupplyModal = (type, row = null) => {
    // La fila está normalizada para la tabla ("-" donde falta dato): al modal va el suministro tal cual.
    const supply = row ? (fincaData.value?.supplies || []).find((item) => item.id === row.id) || row : null;
    supplyModal.value = { open: true, type, supply };
};

// "Crear solicitud" desde cada suministro del inmueble (ADS-0125): el
// formulario recibe el CUPS de origen y fija su energía.
const optionsList = computed(() => supplyRequestOptions(t));

const fincaData = ref(null);
const loading = ref(true);
const error = ref(null);

const showModal = ref(false)
const showDeleteConfirmModal = ref(false)
const isDeleting = ref(false)

const showChangeAccountModal = () => {
    showModal.value = true
}

const openDeleteConfirmModal = () => {
    showDeleteConfirmModal.value = true
}

const closeDeleteConfirmModal = () => {
    if (isDeleting.value) return
    showDeleteConfirmModal.value = false
}

const selectedOption = ref('')

const handleCloseModal = () => {
    showModal.value = false
    selectedOption.value = ''
}
const fetchFincaDetail = async () => {
    if (!id) {
        error.value = 'No ID provided in route.';
        loading.value = false;
        return;
    }
    loading.value = true;
    error.value = null;
    try {
        const response = await axios.get(
            `${import.meta.env.VITE_API_URL}/property/get/${id}`,
            {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
            }
        );
        if (response.data?.value) {
            const Comunidades = response.data.value;
            // Filtra los supplies por tipo
            Comunidades.lights = Comunidades.supplies?.filter(s => s.type === 'light') || [];
            Comunidades.gas = Comunidades.supplies?.filter(s => s.type === 'gas') || [];

            // Procesar los archivos
            if (Comunidades.files) {
                Comunidades.files = Comunidades.files.map(file => {
                    const fileName = file.file_name || '';
                    const fileExtension = fileName.split('.').pop() || '';
                    const fileNameWithoutExtension = fileName.replace(`.${fileExtension}`, '');

                    // Formatear la fecha
                    const date = new Date(file.created_at);
                    const formattedDate = date.toLocaleDateString('es-ES', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    });

                    return {
                        ...file,
                        date: formattedDate,
                        type: fileExtension.toUpperCase(),
                        name: fileNameWithoutExtension,
                        user: file.user || '-',
                        // La documentación de la finca se sirve por su fila, nunca por ruta.
                        preview_url: `${import.meta.env.VITE_API_URL}/property/file/${file.id}/download?disposition=inline`,
                        download_url: `${import.meta.env.VITE_API_URL}/property/file/${file.id}/download`
                    };
                });
            }

            fincaData.value = Comunidades;
        } else {
            fincaData.value = null;
            error.value = 'No se encontraron datos para esta Comunidades.';
        }
    } catch (err) {
        console.error('Error fetching Comunidades detail:', err);
        error.value = 'Failed to load Comunidades details.';
        fincaData.value = null;
    } finally {
        loading.value = false;
    }
};

const router = useRouter();

const deleteFinca = async () => {
    if (!id) {
        error.value = 'No ID provided in route.';
        return;
    }
    isDeleting.value = true
    try {
        const response = await axios.delete(
            `${import.meta.env.VITE_API_URL}/property/delete/${id}`,
            {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
            }
        );
        if (response.data?.value) {
            showDeleteConfirmModal.value = false
            const redirectType = fincaData.value?.type === 'inmueble' ? 'inmueble' : 'comunidad'
            router.push({ path: '/Property', query: { type: redirectType } });
        } else {
            error.value = 'Error al eliminar la Comunidades.';
        }
    } catch (err) {
        console.error('Error deleting Comunidades:', err);
        error.value = 'Error al eliminar la Comunidades.';
    } finally {
        isDeleting.value = false
    }
};

onMounted(() => {
    fetchFincaDetail();
});

</script>

<style scoped>
.contents {
    display: contents;
}
</style>
